import React, { useState } from "react";
import "../assets/css/formFields.css";

/**
 * A labelled text box.
 *
 * The styling lives in formFields.css rather than in an inline style object.
 * It used to be inline, which is why the field had no focus state: inline
 * styles outrank the stylesheet, so the `.custom-input:focus` rule that was
 * already written could never apply.
 *
 * Props are unchanged — label, name, value, onChange, placeholder, type,
 * error, required, autoFocus, width, height, style — plus anything else,
 * which is forwarded to the <input>.
 */
const TextInput = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  required = false,
  autoFocus = false,
  width = "100%",
  height,
  style = {},
  disabled = false,
  prefix,
  suffix,
  /* Pulled out of ...props and re-composed below. Spreading them onto the
     input would replace the handlers that drive the focus ring, and the two
     fields that pass them — the Raised By boxes on Task and Common Team Task,
     which open a suggestion menu — would have been the only ones in the app
     that never lit up. */
  onFocus,
  onBlur,
  ...props
}) => {
  /* Tracked in state as well as via :focus-within so an adornment can react
     too — CSS alone cannot style a sibling's ancestor. */
  const [focused, setFocused] = useState(false);

  return (
    <div className="sk-field" style={{ width }}>
      {label && (
        <label className="sk-field__label" htmlFor={name}>
          {label}
          {required && <span className="sk-field__required">*</span>}
        </label>
      )}

      <div
        className={[
          "sk-field__control",
          focused ? "is-focused" : "",
          error ? "is-invalid" : "",
          disabled ? "is-disabled" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ minHeight: height, ...style }}
      >
        {prefix && <span className="sk-field__affix">{prefix}</span>}

        <input
          id={name}
          type={type}
          name={name}
          className="sk-field__input"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          autoFocus={autoFocus}
          autoComplete="off"
          disabled={disabled}
          aria-invalid={error ? "true" : undefined}
          {...props}
        />

        {suffix && <span className="sk-field__affix">{suffix}</span>}
      </div>

      {error && (
        <div className="sk-field__error">
          <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default TextInput;
